import { useCallback, useEffect, useState } from "react";

function readStorage<T>(key: string): T | undefined {
  // 서버(SSR)에는 localStorage가 없으므로 접근하지 않는다.
  if (typeof window === "undefined") return undefined;
  try {
    const raw = window.localStorage.getItem(key);
    return raw === null ? undefined : (JSON.parse(raw) as T);
  } catch {
    return undefined;
  }
}

/**
 * localStorage에 값을 영속화하는 useState 대체 훅.
 * SSR-safe: 초기값은 항상 defaultValue로 시작해 서버/클라이언트 첫 렌더를 일치시키고,
 * 마운트 후 effect에서 저장값을 복원해 hydration mismatch를 방지한다.
 */
export default function useLocalStorageState<T>(
  key: string,
  defaultValue: T
): [T, (value: T) => void] {
  const [value, setValue] = useState<T>(defaultValue);

  // 마운트(클라이언트) 후 저장값으로 하이드레이트한다.
  useEffect(() => {
    const stored = readStorage<T>(key);
    if (stored !== undefined) setValue(stored);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key]);

  const set = useCallback(
    (next: T) => {
      setValue(next);
      if (typeof window === "undefined") return;
      try {
        window.localStorage.setItem(key, JSON.stringify(next));
      } catch {
        // 저장 실패(용량 초과/사생활 모드 등)는 무시한다.
      }
    },
    [key]
  );

  return [value, set];
}
